import Vue from "vue";
import VueRouter from "vue-router";
import { ApplicationInsights, IConfiguration, IConfig } from "@microsoft/applicationinsights-web";

export interface InsightsPluginOptions {
  iKey: string;
  router?: VueRouter;
  baseName?: string;
  appInsights?: ApplicationInsights;
  appInsightsConfig?: IConfiguration & IConfig;
  trackInitialPageView: boolean;
  onAfterScriptLoaded?(appInsights: ApplicationInsights): void;
}

/**
 * Install function passed to Vue.use() show documentation on vue.js website.
 *
 * @param Vue
 * @param options
 */
function install(_Vue: typeof Vue, options: InsightsPluginOptions = { iKey: "", trackInitialPageView: true }): void {
  if (options.appInsights) {
    _Vue.$appInsights = options.appInsights;
  } else {
    const config = options.appInsightsConfig || {};
    config.instrumentationKey = config.instrumentationKey || options.iKey;

    _Vue.$appInsights = new ApplicationInsights({ config });
    _Vue.$appInsights.loadAppInsights();
    if (typeof options.onAfterScriptLoaded === "function") {
      options.onAfterScriptLoaded(_Vue.$appInsights);
    }
  }

  const router = options.router;

  // Watch route event if router option is defined.
  if (router) {
    if (options.trackInitialPageView !== false) {
      _Vue.$appInsights.trackPageView();
    }
    router.onReady(() => setupPageTracking(options, Vue));
  }

  // eslint-disable-next-line no-prototype-builtins
  if (!_Vue.prototype.hasOwnProperty("$appInsights")) {
    Object.defineProperty(_Vue.prototype, "$appInsights", {
      get: () => _Vue.$appInsights
    });
  }
}

/**
 * Track route changes as page views with AppInsights
 * @param options
 */
function setupPageTracking(options: InsightsPluginOptions, _Vue: typeof Vue) {
  const router = options.router;
  if (!router) return;

  const baseName = options.baseName || window.document.title || "(Vue App)";

  router.beforeEach((to, from, next) => {
    const name = baseName + " / " + to.name;
    _Vue.$appInsights.startTrackPage(name);
    next();
  });

  router.afterEach((to) => {
    const name = baseName + " / " + to.name;
    const url = location.protocol + "//" + location.host + to.fullPath;
    _Vue.$appInsights.stopTrackPage(name, url);
    _Vue.$appInsights.flush();
  });
}

// auto install for navigator
if (typeof window !== "undefined" && window.Vue) {
  window.Vue.use(install);
}

export default install;
